/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import { getDefinitionForType } from '../../elements';
import objectPick from '../../utils/objectPick';
import { getOverlayStyleProperties } from '../../utils/overlayStack';
import { ELEMENT_TYPES } from '../story';
import { normalizeTextBoxBackdropValue } from '../../elements/text/getTextBoxBackdropStyles';

const elementTypes = Object.values(ELEMENT_TYPES);

/**
 * Extracts the styles from an element based off of the element type.
 *
 * @param {Object} element the element
 * @return {Object} an object with a subset of properties of the original element. The properties
 * should all be properties that define styles on that element.
 */
export const getElementStyles = (element) => {
  if (!element || !element?.type || !elementTypes.includes(element.type)) {
    return null;
  }

  const { clearableAttributes } = getDefinitionForType(element.type);
  let styles = objectPick(element, Object.keys(clearableAttributes));

  if ('blendMode' in clearableAttributes && !('blendMode' in styles)) {
    styles = {
      ...styles,
      blendMode: clearableAttributes.blendMode,
    };
  }

  if ('backdropBlur' in clearableAttributes) {
    // Missing optional fields mean an inactive effect, including on old layers.
    styles = {
      ...styles,
      backdropBlur: normalizeTextBoxBackdropValue(element.backdropBlur),
      backdropEdgeSoftness: normalizeTextBoxBackdropValue(
        element.backdropEdgeSoftness
      ),
    };
  }

  if ('overlay' in clearableAttributes || 'overlays' in clearableAttributes) {
    return {
      ...styles,
      ...getOverlayStyleProperties(element),
    };
  }

  return styles;
};

/**
 *
 * @param {string} type the type of the element to update
 * @return {Object|null} the default properties for the element type.
 * Return `null` if `type` isn't valid.
 */
export const getDefaultPropertiesForType = (type) => {
  if (!type || !elementTypes.includes(type)) {
    return null;
  }

  const { clearableAttributes } = getDefinitionForType(type);

  return clearableAttributes;
};

/**
 * Gets the position to move an element to when shifting it one layer
 * forwards or backwards. When the neighbouring layer belongs to a group the
 * element skips the whole group, since the story reducer keeps group layers
 * adjacent and would otherwise snap it straight back.
 *
 * The background element (position 0) is never crossed.
 *
 * @param {Array<Object>} elements The current page elements.
 * @param {number} position Position of the element being moved.
 * @param {number} direction 1 to move forwards, -1 to move backwards.
 * @return {number} The new position, or `position` if it can't move.
 */
export const getAdjacentLayerPosition = (elements, position, direction) => {
  const neighbourPosition = position + direction;
  const neighbour = elements[neighbourPosition];

  if (position < 1 || neighbourPosition < 1 || !neighbour) {
    return position;
  }

  const { groupId } = neighbour;
  if (!groupId || groupId === elements[position].groupId) {
    return neighbourPosition;
  }

  const isInNeighbourGroup = (el) => el.groupId === groupId;
  return direction > 0
    ? elements.findLastIndex(isInNeighbourGroup)
    : elements.findIndex(isInNeighbourGroup);
};
